import React from 'react';
import { Loader2, X } from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';

const DadCampaignModal = ({
  open,
  onClose,
  title,
  detail,
  previewHelperText,
  sequences,
  campaignForm,
  selectedSequenceSteps,
  selectedStep,
  remainingSequenceSteps,
  campaignPreview,
  previewingCampaign,
  loadingSequences,
  campaignSubmitting,
  onCampaignFormChange,
  onChainDelayChange,
  onSubmit,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 p-4">
      <div className="flex min-h-full items-center justify-center">
        <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {detail}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Sequence</label>
              <select
                value={campaignForm.sequence_id}
                onChange={(e) => onCampaignFormChange('sequence_id', e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white"
              >
                {sequences.map((sequence) => (
                  <option key={sequence.sequence_id} value={sequence.sequence_id}>{sequence.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Email In Sequence</label>
              <select
                value={campaignForm.step_key}
                onChange={(e) => onCampaignFormChange('step_key', e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white"
              >
                {selectedSequenceSteps.map((step) => (
                  <option key={step.step_key} value={step.step_key}>{step.step_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Subject</label>
              <Input
                value={campaignPreview?.subject || selectedStep?.subject || ''}
                readOnly
                className="border-gray-300 bg-gray-50 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-200"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Delivery</label>
              <select
                value={campaignForm.delivery_mode}
                onChange={(e) => onCampaignFormChange('delivery_mode', e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white"
              >
                <option value="send_now">Send Now</option>
                <option value="staggered">Stagger Delivery</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Preview</label>
            <Textarea
              value={campaignPreview?.body || selectedStep?.body || ''}
              readOnly
              rows={10}
              className="border-gray-300 bg-gray-50 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-200"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
              {previewHelperText}
            </p>
          </div>

          {campaignForm.delivery_mode === 'staggered' && (
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Batch size</label>
                  <Input
                    type="number"
                    min="1"
                    value={campaignForm.batch_size}
                    onChange={(e) => onCampaignFormChange('batch_size', e.target.value)}
                    className="border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Cadence type</label>
                  <select
                    value={campaignForm.cadence_type}
                    onChange={(e) => onCampaignFormChange('cadence_type', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                  >
                    <option value="daily">Daily</option>
                    <option value="hourly_interval">Hourly Interval</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Cadence interval</label>
                  <Input
                    type="number"
                    min="1"
                    value={campaignForm.cadence_interval}
                    onChange={(e) => onCampaignFormChange('cadence_interval', e.target.value)}
                    className="border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Start date</label>
                  <Input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => onCampaignFormChange('start_date', e.target.value)}
                    className="border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-gray-700 dark:text-gray-300">Start time</label>
                  <Input
                    type="time"
                    value={campaignForm.start_time}
                    onChange={(e) => onCampaignFormChange('start_time', e.target.value)}
                    className="border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {remainingSequenceSteps.length > 0 && (
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={campaignForm.auto_chain_sequence}
                  onChange={(e) => onCampaignFormChange('auto_chain_sequence', e.target.checked)}
                  className="h-4 w-4 rounded accent-aqua"
                />
                Continue the rest of this sequence automatically
              </label>
              {campaignForm.auto_chain_sequence && (
                <div className="mt-3 space-y-3">
                  {remainingSequenceSteps.map((step) => (
                    <div key={step.step_key} className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{step.step_name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Delay after previous email</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          value={campaignForm.chain_delays?.[step.step_key] ?? 0}
                          onChange={(e) => onChainDelayChange(step.step_key, e.target.value)}
                          className="w-24 border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-white"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Recipient Preview</h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {previewingCampaign
                ? 'Loading recipient preview...'
                : 'Showing the first few recipients from the dads you selected.'}
            </p>
            <div className="mt-3 space-y-2">
              {(campaignPreview?.recipients || []).slice(0, 5).map((recipient) => (
                <div key={recipient.user_id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm dark:bg-[#0f0f13]">
                  <span className="text-gray-900 dark:text-white">{recipient.name || 'Unnamed dad'}</span>
                  <span className="text-gray-500 dark:text-gray-400">{recipient.email}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <Badge className="border border-gray-200 bg-white text-gray-700 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-gray-300">
                {campaignPreview?.recipient_count || 0} ready to send
              </Badge>
              <Badge className="border border-gray-200 bg-white text-gray-700 dark:border-[#2a2a32] dark:bg-[#0f0f13] dark:text-gray-300">
                {campaignPreview?.skipped_count || 0} skipped
              </Badge>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              disabled={campaignSubmitting || loadingSequences || previewingCampaign}
              className="bg-aqua text-charcoal hover:bg-aqua-dark"
            >
              {campaignSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {campaignForm.delivery_mode === 'staggered' ? 'Schedule Campaign' : 'Send Now'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DadCampaignModal;
